import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { formatPageReferences } from '../../utils/formatPageReferences';
import { getSVGIconNameFromDocument } from '../../utils/getSVGIconNameFromDocument';
import { triggerDownloadWithAnchorTag } from '../../utils/triggerDownloadWithAnchorTag';
import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { SVGIcon } from '../icons/SVGIcon';

import { displaySourceLabel, formatCitationPreview } from './chatSources';
import { type AiAssistantSource } from './types';

import styles from './SourceCard.module.scss';

export type SourceCardCitation = {
  excerpt?: string;
  number: number;
  pages?: number[];
};

export type SourceCardProps = {
  activeNumber?: number | null;
  citations: SourceCardCitation[];
  flashedNumber?: number | null;
  numbers: number[];
  onDownload?: (source: AiAssistantSource) => void;
  onOpen: (citationNumber: number) => void;
  source: AiAssistantSource;
};

export const SourceCard = forwardRef<HTMLDivElement, SourceCardProps>(
  function SourceCard(
    {
      activeNumber,
      citations,
      flashedNumber,
      numbers,
      onDownload,
      onOpen,
      source,
    },
    ref
  ) {
    const { t } = useTranslation();
    const primaryNumber = numbers[0];
    const cardId =
      primaryNumber != null
        ? String(primaryNumber)
        : source.kind === 'page'
          ? source.to
          : 'source';
    const hasCitations = citations.length > 0;
    const [isExpanded, setIsExpanded] = useState(true);

    useEffect(() => {
      if (
        (flashedNumber != null && numbers.includes(flashedNumber)) ||
        (activeNumber != null && numbers.includes(activeNumber))
      ) {
        setIsExpanded(true);
      }
    }, [activeNumber, flashedNumber, numbers]);

    const openTitle =
      source.kind === 'page'
        ? t('app.aiAssistant.open')
        : t('app.aiAssistant.openInPreview');
    const canOpen =
      source.kind === 'page' ||
      source.file !== undefined ||
      source.documentId !== undefined;

    const canDownload =
      source.kind === 'document' &&
      (source.file !== undefined || source.documentId !== undefined);

    function handleDownload() {
      if (source.kind !== 'document') {
        return;
      }
      if (onDownload) {
        onDownload(source);
        return;
      }
      if (source.file !== undefined) {
        triggerDownloadWithAnchorTag(
          source.file,
          displaySourceLabel(source.label)
        );
      }
    }

    return (
      <div
        className={styles['source-card']}
        data-testid={`ai-assistant-source-card-${cardId}`}
        ref={ref}
      >
        <div className={styles['source-card__head']}>
          {source.kind === 'page' ? (
            <GoogleFontIcon
              className={styles['source-card__icon']}
              name="link"
              solid
            />
          ) : (
            <SVGIcon
              className={styles['source-card__icon']}
              name={getSVGIconNameFromDocument({
                fileName: displaySourceLabel(source.label),
              })}
            />
          )}
          <button
            aria-expanded={hasCitations ? isExpanded : undefined}
            className={styles['source-card__title']}
            data-testid={`ai-assistant-source-toggle-${cardId}`}
            onClick={() => {
              if (hasCitations) {
                setIsExpanded((current) => !current);
                return;
              }
              onOpen(primaryNumber ?? 0);
            }}
            title={
              hasCitations
                ? isExpanded
                  ? t('app.aiAssistant.collapseCitations')
                  : t('app.aiAssistant.expandCitations')
                : openTitle
            }
            type="button"
          >
            {displaySourceLabel(source.label)}
          </button>
          <span className={styles['source-card__spacer']} />
          <div className={styles['source-card__actions']}>
            {canOpen ? (
              <Button
                icon={
                  <GoogleFontIcon
                    name={source.kind === 'page' ? 'open_in_new' : 'visibility'}
                    solid
                  />
                }
                onClick={() => onOpen(primaryNumber ?? 0)}
                title={openTitle}
                variant="text"
                data-testid={`ai-assistant-source-open-${cardId}`}
              />
            ) : null}
            {canDownload ? (
              <Button
                icon={<GoogleFontIcon name="download" solid />}
                onClick={handleDownload}
                title={t('app.aiAssistant.downloadDocument')}
                variant="text"
                data-testid={`ai-assistant-source-download-${primaryNumber}`}
              />
            ) : null}
          </div>
        </div>

        {hasCitations && isExpanded ? (
          <ul className={styles['source-card__citations']}>
            {citations.map((citation) => {
              const pageRef = formatPageReferences(citation.pages);
              return (
                <li key={citation.number}>
                  <button
                    aria-current={
                      activeNumber === citation.number ? 'true' : undefined
                    }
                    className={clsx(styles['source-card__citation'], {
                      [styles['source-card__citation--flash']]:
                        flashedNumber === citation.number,
                      [styles['source-card__citation--active']]:
                        activeNumber === citation.number,
                    })}
                    data-testid={`ai-assistant-source-citation-${citation.number}`}
                    onClick={() => onOpen(citation.number)}
                    title={t('app.aiAssistant.jumpToSource', {
                      index: String(citation.number),
                    })}
                    type="button"
                  >
                    <span className={styles['source-card__badge']}>
                      {citation.number}
                    </span>
                    <span className={styles['source-card__citation-body']}>
                      {citation.excerpt ? (
                        <span className={styles['source-card__excerpt']}>
                          {formatCitationPreview(citation.excerpt)}
                        </span>
                      ) : null}
                      {pageRef ? (
                        <span className={styles['source-card__pages']}>
                          {pageRef}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    );
  }
);
